/**
 * Inspector / Workbench shell state (Phase 1 of #268 / #272, sidebar + Roadmap in #280).
 *
 * Pure layout preferences and selection rules — the room passes storage in so
 * the rules can be tested without a browser. Persistence follows the same
 * pattern as the rail and cockpit layout: one key, JSON when needed, quiet
 * failure when storage is refused.
 */

import type { AutomaticHandoff } from '@brunel/shared';

export type InspectorMode = 'hidden' | 'drawer' | 'docked';
export type InspectorTab =
  | 'observer'
  | 'notes'
  | 'workboard'
  | 'roadmap'
  | 'results'
  | 'usage'
  | 'systems'
  | 'machines'
  | 'work_result'
  | 'handoff'
  | 'preview';

export interface InspectorTabDef {
  readonly id: InspectorTab;
  readonly label: string;
  /** Compact label when the internal sidebar is collapsed to icons. */
  readonly short: string;
}

/** Tools in the Inspector's internal left sidebar. */
export const INSPECTOR_TABS: readonly InspectorTabDef[] = [
  { id: 'notes', label: 'Notes', short: 'NT' },
  { id: 'workboard', label: 'Work Board', short: 'WB' },
  { id: 'roadmap', label: 'Roadmap', short: 'RM' },
  { id: 'results', label: 'Results', short: 'RS' },
  { id: 'usage', label: 'AI Usage', short: 'AI' },
  { id: 'systems', label: 'Systems', short: 'SY' },
  { id: 'machines', label: 'Machine Capacity', short: 'MC' },
  { id: 'work_result', label: 'Work Result', short: 'WR' },
  { id: 'handoff', label: 'Handoff', short: 'HO' },
  { id: 'preview', label: 'Preview', short: 'PV' },
] as const;

/** Right-rail buttons are real toggles: same open tool collapses; any other tool opens/switches. */
export function activateInspectorTool(
  mode: InspectorMode,
  tab: InspectorTab,
  next: InspectorTab,
): { readonly mode: InspectorMode; readonly tab: InspectorTab } {
  if (mode !== 'hidden' && tab === next) return { mode: 'hidden', tab };
  return { mode: 'drawer', tab: next };
}

/** Narrowest/default Inspector size in pixels. The maximum is viewport-relative. */
export const INSPECTOR_MIN_PX = 280;
export const INSPECTOR_DEFAULT_PX = 380;
export const INSPECTOR_MAX_VIEWPORT_SHARE = 0.82;
export const INSPECTOR_SAFE_MAIN_PX = 280;
export const INSPECTOR_MOBILE_FULL_WIDTH_PX = 720;

/** Below this viewport width, Dock falls back to drawer (third column needs room). */
export const INSPECTOR_DOCK_MIN_VIEWPORT_PX = 1280;

/** Collapsed permanent rail width (square icons). */
export const INSPECTOR_RAIL_COLLAPSED_PX = 48;
/** Expanded rail width when tool names are shown beside icons. */
export const INSPECTOR_RAIL_LABELS_PX = 152;

export interface InspectorSelection {
  readonly workerId: string | null;
  readonly workerLabel: string | null;
  readonly taskId: string | null;
  readonly taskTitle: string | null;
  readonly previewUrl: string | null;
  readonly handoff: AutomaticHandoff | null;
  readonly nodeId: string | null;
  readonly nodeName: string | null;
  readonly projectId: string | null;
  readonly projectName: string | null;
  readonly sessionId: string | null;
  readonly branch: string | null;
  readonly filesChanged: readonly string[] | null;
  readonly patchText: string | null;
  readonly runSummary: string | null;
}

export const EMPTY_INSPECTOR_SELECTION: InspectorSelection = {
  workerId: null,
  workerLabel: null,
  taskId: null,
  taskTitle: null,
  previewUrl: null,
  handoff: null,
  nodeId: null,
  nodeName: null,
  projectId: null,
  projectName: null,
  sessionId: null,
  branch: null,
  filesChanged: null,
  patchText: null,
  runSummary: null,
};

/** The little of the Inspector the owner wants to survive a reload. */
export interface InspectorRemembered {
  readonly mode: InspectorMode;
  readonly widthPx: number;
  readonly tab: InspectorTab;
  readonly followSelection: boolean;
  readonly pinnedWorkerId: string | null;
  readonly pinnedTaskId: string | null;
  readonly sidebarCollapsed: boolean;
  /** When true, the permanent right rail shows icon + full tool name. */
  readonly railLabels: boolean;
}

export const DEFAULT_INSPECTOR: InspectorRemembered = {
  mode: 'hidden',
  widthPx: INSPECTOR_DEFAULT_PX,
  tab: 'roadmap',
  followSelection: true,
  pinnedWorkerId: null,
  pinnedTaskId: null,
  sidebarCollapsed: false,
  railLabels: false,
};

const KEY = 'brunel.command.inspector';

interface StringStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function isInspectorMode(value: unknown): value is InspectorMode {
  return value === 'hidden' || value === 'drawer' || value === 'docked';
}

export function isInspectorTab(value: unknown): value is InspectorTab {
  if (typeof value !== 'string') return false;
  if (value === 'observer') return true; // legacy / visual fixtures only — not on the owner rail
  return INSPECTOR_TABS.some((tab) => tab.id === value);
}

export function inspectorMaxWidth(
  viewportPx: number,
  railWidthPx = INSPECTOR_RAIL_COLLAPSED_PX,
): number {
  const viewport = Number.isFinite(viewportPx) ? Math.max(0, viewportPx) : 1440;
  const rail = Number.isFinite(railWidthPx) ? Math.max(0, railWidthPx) : INSPECTOR_RAIL_COLLAPSED_PX;
  const usable = Math.max(0, viewport - rail);
  if (viewport <= INSPECTOR_MOBILE_FULL_WIDTH_PX || usable <= INSPECTOR_MIN_PX) return Math.round(usable);
  const shareMax = Math.floor(viewport * INSPECTOR_MAX_VIEWPORT_SHARE);
  const safeMainMax = Math.floor(viewport - INSPECTOR_SAFE_MAIN_PX - rail);
  return Math.max(INSPECTOR_MIN_PX, Math.min(usable, shareMax, safeMainMax));
}

export function clampInspectorWidth(
  px: number,
  viewportPx = 1440,
  railWidthPx = INSPECTOR_RAIL_COLLAPSED_PX,
): number {
  const desired = Number.isFinite(px) ? px : INSPECTOR_DEFAULT_PX;
  const max = inspectorMaxWidth(viewportPx, railWidthPx);
  if (viewportPx <= INSPECTOR_MOBILE_FULL_WIDTH_PX || max <= INSPECTOR_MIN_PX) {
    return Math.round(Math.max(0, max));
  }
  return Math.round(Math.min(max, Math.max(INSPECTOR_MIN_PX, desired)));
}

function rememberedInspectorWidth(px: number): number {
  if (!Number.isFinite(px)) return INSPECTOR_DEFAULT_PX;
  return Math.round(Math.max(INSPECTOR_MIN_PX, px));
}

/**
 * Dock is only meaningful on a wide desk. On a narrower viewport the control
 * still says Dock, but the shell opens as a drawer so Command + Workers stay usable.
 */
export function effectiveInspectorMode(mode: InspectorMode, viewportPx: number): InspectorMode {
  if (mode === 'docked' && viewportPx < INSPECTOR_DOCK_MIN_VIEWPORT_PX) return 'drawer';
  return mode;
}

export function recallInspector(store: StringStore | null): InspectorRemembered {
  if (store === null) return DEFAULT_INSPECTOR;
  let raw: string | null = null;
  try {
    raw = store.getItem(KEY);
  } catch {
    return DEFAULT_INSPECTOR;
  }
  if (raw === null) return DEFAULT_INSPECTOR;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return DEFAULT_INSPECTOR;
  }
  if (typeof parsed !== 'object' || parsed === null) return DEFAULT_INSPECTOR;
  const row = parsed as Record<string, unknown>;
  const widthPx = typeof row['widthPx'] === 'number' ? rememberedInspectorWidth(row['widthPx']) : INSPECTOR_DEFAULT_PX;
  const legacyTab = row['tab'];
  const tab: InspectorTab = isInspectorTab(legacyTab)
    ? legacyTab
    : (legacyTab === 'files' || legacyTab === 'diff' || legacyTab === 'evidence'
      ? 'work_result'
      : DEFAULT_INSPECTOR.tab);
  return {
    mode: isInspectorMode(row['mode']) ? row['mode'] : DEFAULT_INSPECTOR.mode,
    widthPx,
    tab: legacyTab === 'computers' ? DEFAULT_INSPECTOR.tab : tab,
    followSelection: row['followSelection'] === false ? false : true,
    pinnedWorkerId: typeof row['pinnedWorkerId'] === 'string' ? row['pinnedWorkerId'] : null,
    pinnedTaskId: typeof row['pinnedTaskId'] === 'string' ? row['pinnedTaskId'] : null,
    sidebarCollapsed: row['sidebarCollapsed'] === true,
    railLabels: row['railLabels'] === true,
  };
}

export function rememberInspector(store: StringStore | null, state: InspectorRemembered): void {
  if (store === null) return;
  try {
    store.setItem(KEY, JSON.stringify({
      mode: state.mode,
      widthPx: rememberedInspectorWidth(state.widthPx),
      tab: state.tab,
      followSelection: state.followSelection,
      pinnedWorkerId: state.pinnedWorkerId,
      pinnedTaskId: state.pinnedTaskId,
      sidebarCollapsed: state.sidebarCollapsed,
      railLabels: state.railLabels,
    }));
  } catch {
    /* a browser with storage turned off simply does not remember */
  }
}

/** Safe http(s) preview URL, or null when the value is empty / unsafe. */
export function safePreviewUrl(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

/** Reserved permanent rail width for the workzone clearance. */
export function inspectorRailWidthPx(railLabels: boolean): number {
  return railLabels ? INSPECTOR_RAIL_LABELS_PX : INSPECTOR_RAIL_COLLAPSED_PX;
}
